import { CP_SERVICE_IDENTITIES, DP_TRANSPORT_MODES, DP_TRANSPORT_RESOLVE_FAILURES, HTTPS_BASE_URL_PATTERN, PUBLIC_MTLS_BASE_URL_PATTERN, PUBLIC_MTLS_PORT, isHttpsBaseUrl } from "./dp-transport.js";
import { doorPullDataPlaneIdSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/dp-transport.schemas.ts
/**
* The zod rules of the CP-to-DP transport contract (plan 2026-10-27-cp-dp-network-path, WS-2). The
* types, the constants and the pure functions are in `dp-transport.ts`; this file holds the runtime
* validation, which is this package's convention (`door-pull.ts` and `door-pull.schemas.ts`).
*
* Each rule matches its type in `dp-transport.ts`; `dp-transport.test.ts` holds the two equal at
* compile time. No rule here uses `.transform()`. Each string rule is a length or a `.regex()`, so
* `z.toJSONSchema` writes it as `minLength`, `maxLength` or `pattern`, and a JSON Schema validator
* refuses what the zod schema refuses. The one rule beyond JSON Schema is the host check of the
* URL parser in `isHttpsBaseUrl`, which the zod schema adds to the URL pattern.
*/
/** A transport mode: one of `DP_TRANSPORT_MODES`, exact case. */
const DpTransportModeSchema = z.enum(DP_TRANSPORT_MODES);
/**
* A data plane id: a lower-case UUID (`isDpId`), the pattern of `doorPullDataPlaneIdSchema`, and
* exactly 36 characters. The length bound keeps a final line feed out also for a JSON Schema
* validator whose `$` matches before a final line feed.
*/
const DpIdSchema = doorPullDataPlaneIdSchema.length(36);
/**
* A base URL: the text of `HTTPS_BASE_URL_PATTERN` (a plain `https:` URL with no trailing `/`),
* and a host that the URL parser accepts (`isHttpsBaseUrl`). Its text holds no `?`, `#`, `@`, `\`,
* white space, control character or character outside ASCII.
*/
const BaseUrlSchema = z.string().regex(HTTPS_BASE_URL_PATTERN, "an https base URL with no user info, query, fragment or trailing slash").refine(isHttpsBaseUrl, "an https base URL whose host the URL parser accepts");
/**
* A `public-mtls` transport. Strict. `url` matches `PUBLIC_MTLS_BASE_URL_PATTERN`, the base URL
* rule with the explicit port `PUBLIC_MTLS_PORT` (8444), and passes `isHttpsBaseUrl`.
* `sniHostname` is 1 to 253 characters.
*/
const PublicMtlsDpTransportSchema = z.object({
	mode: z.literal("public-mtls"),
	dpId: DpIdSchema,
	url: z.string().regex(PUBLIC_MTLS_BASE_URL_PATTERN, `an https base URL on port ${PUBLIC_MTLS_PORT} with no user info, query, fragment or trailing slash`).refine(isHttpsBaseUrl, "an https base URL whose host the URL parser accepts"),
	sniHostname: z.string().min(1).max(253)
}).strict();
/** A `tunnel` transport. Strict. */
const TunnelDpTransportSchema = z.object({
	mode: z.literal("tunnel"),
	dpId: DpIdSchema,
	terminatorUrl: BaseUrlSchema
}).strict();
/**
* A resolver answer: one of the two arms, chosen by `mode`. An unknown key, an unknown mode or a
* value that breaks a rule of its arm fails with a `ZodError`. The contract suite of `./testing`
* parses each resolver answer with it.
*/
const DpTransportSchema = z.discriminatedUnion("mode", [PublicMtlsDpTransportSchema, TunnelDpTransportSchema]);
/** A resolve refusal reason: one of `DP_TRANSPORT_RESOLVE_FAILURES`. */
const DpTransportResolveFailureSchema = z.enum(DP_TRANSPORT_RESOLVE_FAILURES);
/** A CP service identity: one of `CP_SERVICE_IDENTITIES`, exact case. */
const CpServiceIdentitySchema = z.enum(CP_SERVICE_IDENTITIES);
//#endregion
export { CpServiceIdentitySchema, DpTransportModeSchema, DpTransportResolveFailureSchema, DpTransportSchema, PublicMtlsDpTransportSchema, TunnelDpTransportSchema };

//# sourceMappingURL=dp-transport.schemas.js.map